//
// # CRDT Map
//
// CRDT-aware entity map with timestamps and tombstones for collaborative editing.
// Implements IEntityMap for interchangeability with IdMap.
//
// Uses Last-Write-Wins (LWW) semantics for conflict resolution.
// Tombstones are used for deletions to handle concurrent delete/update conflicts.
//

import {
  type LamportTimestamp,
  type CRDTEntity,
  compareTimestamp,
  createCRDTEntity,
  mergeCRDTEntities,
} from "CoreUtils";
import { IEntityMap, HasId } from "./entity_map";
import { randomId } from "./id";

/**
 * CRDT-aware entity map with timestamps and tombstones.
 *
 * Used when collaboration is enabled for a tenant.
 * Implements same interface as IdMap for interchangeability.
 *
 * Additional CRDT-specific methods:
 * - merge(): Merge remote state into local state
 * - receiveTick(): Update local clock based on remote timestamp
 * - getWithMetadata(): Get entity with CRDT metadata
 * - gcTombstones(): Garbage collect old tombstones
 */
export class CRDTMap<T extends HasId> implements IEntityMap<T> {
  private readonly _map = new Map<number, CRDTEntity<T>>();
  private _clock: number = 0;
  private readonly _clientId: string;

  constructor(clientId: string, initialClock: number = 0) {
    this._clientId = clientId;
    this._clock = initialClock;
  }

  /** Get the current Lamport clock value */
  get clock(): number {
    return this._clock;
  }

  /** Get the client ID */
  get clientId(): string {
    return this._clientId;
  }

  /**
   * Advance the clock and create a new timestamp.
   * Used when the local client performs an operation.
   */
  private tick(): LamportTimestamp {
    this._clock++;
    return { counter: this._clock, clientId: this._clientId };
  }

  /**
   * Update local clock based on a received remote timestamp.
   * Ensures the local clock is always ahead of any observed remote clock.
   */
  receiveTick(remote: LamportTimestamp): void {
    this._clock = Math.max(this._clock, remote.counter) + 1;
  }

  // ========================================================================
  // IEntityMap implementation
  // ========================================================================

  add = (val: T, timestamp?: LamportTimestamp): void => {
    const ts = timestamp ?? this.tick();
    // Observing an externally-timestamped op must advance our Lamport clock
    // (max(clock, ts)+1). Without this, `clock` lags behind entities'
    // lastModified after remote ops, so getMaxEntityMapClock under-reports and
    // a rejoining client's next op loses LWW and is silently dropped.
    if (timestamp) this.receiveTick(timestamp);
    const id = val.id ?? randomId();
    val.id = id;

    const existing = this._map.get(id);
    if (existing) {
      // ID collision (rare with random IDs) - use later timestamp
      if (compareTimestamp(ts, existing.lastModified) > 0) {
        this._map.set(id, createCRDTEntity(val, ts, this._clientId));
      }
      // If existing is newer, silently ignore this add
    } else {
      this._map.set(id, createCRDTEntity(val, ts, this._clientId));
    }
  };

  get = (id: number): T | undefined => {
    const entity = this._map.get(id);
    if (!entity || entity.deleted) return undefined;
    return entity.value;
  };

  update = (
    val: Partial<T> & { id: number },
    timestamp?: LamportTimestamp
  ): boolean => {
    const ts = timestamp ?? this.tick();
    // Advance our clock to observe an external op's timestamp (see add()).
    if (timestamp) this.receiveTick(timestamp);
    const { id } = val;
    const existing = this._map.get(id);

    // Entity never existed - this is a programming error
    if (!existing) return false;

    // Entity was deleted by another client - conflict resolved (delete wins)
    // Return true because the operation was "handled" by CRDT semantics
    if (existing.deleted) return true;

    // Only apply update if our timestamp is newer
    if (compareTimestamp(ts, existing.lastModified) > 0) {
      this._map.set(id, {
        value: { ...existing.value, ...val },
        lastModified: ts,
        modifiedBy: this._clientId,
        deleted: false,
      });
      return true;
    }

    // Our change is older than what's already there - conflict resolved (newer wins)
    // Return true because the operation was "handled" by CRDT semantics
    return true;
  };

  remove = (id: number, timestamp?: LamportTimestamp): boolean => {
    const ts = timestamp ?? this.tick();
    // Advance our clock to observe an external op's timestamp (see add()).
    if (timestamp) this.receiveTick(timestamp);
    const existing = this._map.get(id);

    // Entity never existed - this could be a programming error or race condition
    // Return false so caller can handle appropriately
    if (!existing) return false;

    // Create tombstone if this delete is newer
    if (!existing.deleted || compareTimestamp(ts, existing.deletedAt!) > 0) {
      this._map.set(id, {
        ...existing,
        deleted: true,
        deletedAt: ts,
      });
      return true;
    }

    // Already deleted with a newer timestamp - conflict resolved (newer wins)
    // Return true because the operation was "handled" by CRDT semantics
    return true;
  };

  forEach = (fn: (val: T) => void): void => {
    this._map.forEach((entity) => {
      if (!entity.deleted) fn(entity.value);
    });
  };

  values = (): IterableIterator<T> => {
    const self = this;
    return (function* () {
      for (const entity of self._map.values()) {
        if (!entity.deleted) yield entity.value;
      }
    })();
  };

  size = (): number => {
    let count = 0;
    this._map.forEach((entity) => {
      if (!entity.deleted) count++;
    });
    return count;
  };

  // ========================================================================
  // CRDT-specific methods (not part of IEntityMap interface)
  // ========================================================================

  /**
   * Get an entity with its CRDT metadata.
   * Useful for debugging or when you need timestamp information.
   */
  getWithMetadata(id: number): CRDTEntity<T> | undefined {
    return this._map.get(id);
  }

  /**
   * Check if an entity exists (including tombstones).
   */
  has(id: number): boolean {
    return this._map.has(id);
  }

  /**
   * Check if an entity has been deleted (is a tombstone).
   */
  isDeleted(id: number): boolean {
    const entity = this._map.get(id);
    return entity?.deleted ?? false;
  }

  /**
   * Get count including tombstones.
   */
  sizeWithTombstones(): number {
    return this._map.size;
  }

  /**
   * Get count of tombstones only.
   */
  tombstoneCount(): number {
    let count = 0;
    this._map.forEach((entity) => {
      if (entity.deleted) count++;
    });
    return count;
  }

  /**
   * Merge a single remote entity into this map.
   * Uses LWW semantics for conflict resolution.
   */
  mergeEntity(id: number, remote: CRDTEntity<T>): void {
    const local = this._map.get(id);

    if (!local) {
      // New entity from remote
      this._map.set(id, remote);
      this.receiveTick(remote.lastModified);
    } else {
      // Both have this entity - merge based on timestamps
      const merged = mergeCRDTEntities(local, remote);
      this._map.set(id, merged);
      this.receiveTick(remote.lastModified);
    }
  }

  /**
   * Merge all entities from another CRDTMap into this one.
   * Used for syncing state between clients.
   */
  merge(remote: CRDTMap<T>): void {
    for (const [id, remoteEntity] of remote._map) {
      this.mergeEntity(id, remoteEntity);
    }
  }

  /**
   * Garbage collect tombstones older than the given threshold.
   * Only call this when you're certain no client will need the tombstone.
   * @param threshold Timestamp threshold - tombstones older than this are removed
   */
  gcTombstones(threshold: LamportTimestamp): number {
    let removed = 0;
    const toRemove: number[] = [];

    this._map.forEach((entity, id) => {
      if (
        entity.deleted &&
        entity.deletedAt &&
        compareTimestamp(entity.deletedAt, threshold) < 0
      ) {
        toRemove.push(id);
      }
    });

    for (const id of toRemove) {
      this._map.delete(id);
      removed++;
    }

    return removed;
  }

  /**
   * Clear all tombstones (for session-scoped GC).
   */
  clearTombstones(): number {
    let removed = 0;
    const toRemove: number[] = [];

    this._map.forEach((entity, id) => {
      if (entity.deleted) {
        toRemove.push(id);
      }
    });

    for (const id of toRemove) {
      this._map.delete(id);
      removed++;
    }

    return removed;
  }

  /**
   * Export all entities (including tombstones) for serialization.
   */
  exportAll(): Array<[number, CRDTEntity<T>]> {
    return Array.from(this._map.entries());
  }

  /**
   * Import entities from serialized data.
   * Merges with existing data using LWW semantics.
   */
  importAll(entries: Array<[number, CRDTEntity<T>]>): void {
    for (const [id, entity] of entries) {
      this.mergeEntity(id, entity);
    }
  }

  /**
   * Export only non-deleted entities as plain values (for depot save).
   * Strips CRDT metadata.
   */
  exportValues(): T[] {
    const result: T[] = [];
    this._map.forEach((entity) => {
      if (!entity.deleted) {
        result.push(entity.value);
      }
    });
    return result;
  }

  /**
   * Create a snapshot of current state for rollback.
   */
  snapshot(): Map<number, CRDTEntity<T>> {
    return new Map(this._map);
  }

  /**
   * Restore from a snapshot.
   */
  restoreSnapshot(snapshot: Map<number, CRDTEntity<T>>): void {
    this._map.clear();
    for (const [id, entity] of snapshot) {
      this._map.set(id, entity);
    }
  }
}
